import { useCallback, useRef, useState } from 'react';
import { calmTokens as t } from '../shared/tokens';
import { IconX, IconCheck } from '../shared/icons';
import { NAV_ITEMS } from '../shared/nav';
import useBodyScrollLock from '../hooks/useBodyScrollLock';
import useDismissable from '../hooks/useDismissable';

export default function BurgerMenu({ currentView, onViewChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef(null);

  const handleItemClick = (viewId) => {
    onViewChange(viewId);
    setIsOpen(false);
  };

  const close = useCallback(() => setIsOpen(false), []);

  useBodyScrollLock(isOpen);
  useDismissable({ open: isOpen, onClose: close, containerRef: panelRef });

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={isOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
        style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          background: t.surface,
          border: `1px solid ${t.border}`,
          color: t.text,
          cursor: 'pointer',
          display: 'grid',
          placeItems: 'center',
          padding: 0,
          transition: 'all .15s',
        }}
      >
        <div
          style={{
            width: 18,
            height: 14,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <span
            style={{
              width: '100%',
              height: 2,
              background: t.text,
              borderRadius: 1,
              transformOrigin: 'center',
              transition: 'transform .25s ease, opacity .25s ease',
              transform: isOpen ? 'rotate(45deg) translate(4px, 4px)' : 'none',
            }}
          />
          <span
            style={{
              width: '100%',
              height: 2,
              background: t.text,
              borderRadius: 1,
              transition: 'opacity .2s ease',
              opacity: isOpen ? 0 : 1,
            }}
          />
          <span
            style={{
              width: '100%',
              height: 2,
              background: t.text,
              borderRadius: 1,
              transformOrigin: 'center',
              transition: 'transform .25s ease',
              transform: isOpen ? 'rotate(-45deg) translate(4px, -4px)' : 'none',
            }}
          />
        </div>
      </button>

      {isOpen && (
        <div
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            zIndex: 40,
          }}
        />
      )}

      <aside
        ref={panelRef}
        className="calm-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Navigation"
        tabIndex={-1}
        style={{
          background: t.bg,
          borderRight: `1px solid ${t.borderS}`,
          boxShadow: '0 40px 80px rgba(0,0,0,0.5)',
          transform: isOpen ? 'translateX(0)' : 'translateX(-100%)',
          // `aria-hidden` seul laisserait les boutons dans le parcours Tab :
          // `visibility` les en retire réellement quand le tiroir est fermé.
          visibility: isOpen ? 'visible' : 'hidden',
          fontFamily: t.fontUi,
        }}
        aria-hidden={!isOpen}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px 22px',
            borderBottom: `1px solid ${t.borderS}`,
          }}
        >
          <h2
            style={{
              fontFamily: t.fontDisp,
              fontSize: 18,
              fontWeight: 500,
              letterSpacing: -0.3,
              color: t.text,
              margin: 0,
            }}
          >
            Navigation
          </h2>
          <button
            type="button"
            onClick={close}
            aria-label="Fermer le menu"
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: 'transparent',
              border: `1px solid ${t.border}`,
              color: t.textMute,
              cursor: 'pointer',
              display: 'grid',
              placeItems: 'center',
              padding: 0,
            }}
          >
            <IconX size={14} />
          </button>
        </div>

        <nav
          style={{
            padding: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {NAV_ITEMS.map((item) => {
            const isActive = currentView === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => handleItemClick(item.id)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  padding: '14px 16px',
                  borderRadius: 12,
                  background: isActive ? t.surfaceH : t.surface,
                  border: `1px solid ${isActive ? t.borderS : t.border}`,
                  color: isActive ? t.text : t.textDim,
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontFamily: t.fontUi,
                  transition: 'all .15s',
                }}
              >
                <span
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 10,
                    background: isActive ? t.accentDim : 'transparent',
                    border: `1px solid ${isActive ? 'rgba(165,180,252,0.28)' : t.border}`,
                    display: 'grid',
                    placeItems: 'center',
                    color: isActive ? t.accent : t.textDim,
                    flexShrink: 0,
                  }}
                >
                  {item.icon(16)}
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 500, color: t.text }}>
                    {item.label}
                  </div>
                  <div style={{ fontSize: 12, color: t.textMute, marginTop: 2 }}>
                    {item.description}
                  </div>
                </div>
                {isActive && (
                  <span style={{ color: t.accent }}>
                    <IconCheck size={16} />
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        <div
          style={{
            marginTop: 'auto',
            padding: 16,
            borderTop: `1px solid ${t.borderS}`,
            textAlign: 'center',
            fontSize: 11,
            color: t.textMute,
            letterSpacing: 0.2,
          }}
        >
          Ma Comptabilité · v2.0
        </div>
      </aside>
    </>
  );
}
